<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>Edit Downtime Report</v-toolbar-title>

        <v-spacer></v-spacer>
        <v-btn color="white" small text @click="printReport(downtimeReportId)">
          <v-icon left>mdi-printer</v-icon>
          Print
        </v-btn>
        <!-- Back Button -->
        <v-btn color="white" small text :to="'/downtime-reports'">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-form ref="mainFormRef">
          <v-row dense>
            <!-- Service Report -->
            <v-col cols="12">
              <v-switch
                inset
                color="primary"
                label="Service Report"
                v-model="inputs.is_service_report"
              />
            </v-col>

            <!-- Customer -->
            <v-col cols="12" md="6">
              <v-autocomplete
                outlined
                hide-details="auto"
                dense
                label="Customer"
                name="customer_id"
                item-value="id"
                :item-text="item => `${item.id} - ${item.name}`"
                :items="customers"
                v-model="inputs.customer_id"
                :error-messages="errors.customer_id"
              ></v-autocomplete>
            </v-col>

            <!-- Unit -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Unit"
                name="unit_id"
                v-model="inputs.unit_id"
                :error-messages="errors.unit_id"
              />
            </v-col>

            <!-- Subject -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Subject"
                name="subject"
                v-model="inputs.subject"
                :error-messages="errors.subject"
              />
            </v-col>
            <!-- Status -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                hide-details="auto"
                dense
                label="Status"
                name="status"
                :items="statusOptions"
                v-model="inputs.status"
                :error-messages="errors.status"
              >
                <template #selection="{ item }">
                  <v-chip small :color="statusColor(item)" dark class="text-capitalize">
                    {{ item || '—' }}
                  </v-chip>
                </template>
                <template #item="{ item }">
                  <v-chip small :color="statusColor(item)" dark class="text-capitalize">
                    {{ item }}
                  </v-chip>
                </template>
              </v-select>
            </v-col>

            <!-- Issue / Error -->
            <v-col cols="12" md="6" v-if="!inputs.is_service_report">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Reported Issue"
                name="reported_issue"
                v-model="inputs.reported_issue"
                :error-messages="errors.reported_issue"
              />
            </v-col>

            <v-col cols="12" md="6" v-if="!inputs.is_service_report">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Error Code"
                name="error"
                v-model="inputs.error"
                :error-messages="errors.error"
              />
            </v-col>
            <!-- Description -->
            <v-col cols="12">
              <v-textarea
                outlined
                hide-details="auto"
                dense
                rows="3"
                auto-grow
                label="Description"
                name="description"
                v-model="inputs.description"
                :error-messages="errors.description"
              />
            </v-col>

            <!-- Machine Type -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                hide-details="auto"
                dense
                label="Machine Type"
                name="machine_type_id"
                :items="machineTypes"
                item-value="id"
                item-text="name"
                v-model="inputs.machine_type_id"
                :error-messages="errors.machine_type_id"
              />
            </v-col>
            <!-- Reported At -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Reported At"
                name="reported_at"
                type="datetime-local"
                v-model="inputs.reported_at"
                :error-messages="errors.reported_at"
              />
            </v-col>
            <!-- Date Start -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Date Start"
                name="date_start"
                type="datetime-local"
                v-model="inputs.date_start"
                :error-messages="errors.date_start"
              />
            </v-col>

            <!-- Date End -->
            <v-col cols="12" md="6">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                label="Date End"
                name="date_end"
                type="datetime-local"
                v-model="inputs.date_end"
                :error-messages="errors.date_end"
              />
            </v-col>

            <!-- Status Before -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                hide-details="auto"
                dense
                label="Status Before"
                name="list_status_before"
                :items="['operational', 'non-operational']"
                v-model="inputs.list_status_before"
                :error-messages="errors.list_status_before"
              />
            </v-col>

            <!-- Status After -->
            <v-col cols="12" md="6">
              <v-select
                outlined
                hide-details="auto"
                dense
                label="Status After"
                name="list_status_after"
                :items="['operational', 'non-operational']"
                v-model="inputs.list_status_after"
                :error-messages="errors.list_status_after"
              />
            </v-col>
          </v-row>

          <!-- Inspection Findings -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Inspection Findings</h4>
          <v-row dense>
            <v-col cols="12" v-for="(finding, i) in inspectionFindings" :key="'finding-' + i">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                :label="`Finding ${i + 1}`"
                v-model="inspectionFindings[i]"
                append-icon="mdi-delete"
                @click:append="inspectionFindings.splice(i, 1)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="inspectionFindings.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Finding
              </v-btn>
            </v-col>
          </v-row>

          <!-- Actions -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Actions</h4>
          <v-row dense>
            <v-col cols="12" v-for="(action, i) in actions" :key="'action-' + i">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                :label="`Action ${i + 1}`"
                v-model="actions[i]"
                append-icon="mdi-delete"
                :error-messages="errors[`actions.${i}.action`]"
                @click:append="removeAction(i)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="actions.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Action
              </v-btn>
            </v-col>
          </v-row>
          <!-- Recommendations -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">Recommendations</h4>
          <v-row dense>
            <v-col cols="12" v-for="(rec, i) in recommendations" :key="'rec-' + i">
              <v-text-field
                outlined
                hide-details="auto"
                dense
                :label="`Recommendation ${i + 1}`"
                v-model="recommendations[i]"
                append-icon="mdi-delete"
                @click:append="recommendations.splice(i, 1)"
              />
            </v-col>
            <v-col cols="12">
              <v-btn small color="green" @click="recommendations.push('')">
                <v-icon left small>mdi-plus</v-icon>Add Recommendation
              </v-btn>
            </v-col>
          </v-row>

          <!-- Purchase Needed -->
          <v-divider class="my-4"></v-divider>
          <v-switch
            inset
            label="Purchase Needed"
            v-model="inputs.purchase_needed"
            :true-value="true"
            hide-details="auto"
            :false-value="false"
          ></v-switch>
          <!-- IMAGES -->
          <v-divider class="my-4"></v-divider>

          <h4 class="text-subtitle-1 font-weight-medium mb-2">
            Images
          </h4>

          <v-row dense v-for="(row, rowIndex) in imageRows" :key="'row-' + rowIndex" align="center">
            <v-col
              v-for="(img, i) in row"
              :key="'img-' + rowIndex + '-' + i"
              :cols="12"
              :sm="Math.floor(spanToNumber(img.span) * 12)"
            >
              <div class="d-flex flex-column" style="gap: 10px">
                <!-- IMAGE NAME -->
                <v-text-field
                  outlined
                  dense
                  hide-details="auto"
                  label="Image Name"
                  v-model="img.name"
                  :error-messages="errors[`images.${getImageIndex(img)}.name`]"
                />

                <!-- FILE -->
                <v-file-input
                  outlined
                  dense
                  hide-details="auto"
                  accept="image/*"
                  prepend-inner-icon="mdi-image"
                  append-icon=""
                  label="Image"
                  @change="onFileChange($event, img)"
                  :error-messages="errors[`images.${getImageIndex(img)}.file`]"
                />

                <!-- SPAN -->
                <v-select
                  outlined
                  dense
                  hide-details="auto"
                  label="Span"
                  :items="spanOptions"
                  item-text="label"
                  item-value="value"
                  v-model="img.span"
                  :error-messages="errors[`images.${getImageIndex(img)}.span`]"
                />
              </div>

              <!-- PREVIEW -->
              <v-img
                v-if="img.preview"
                :src="img.preview"
                max-height="220"
                class="mt-2 rounded"
                contain
              />

              <!-- REMOVE -->
              <v-btn icon small color="red" class="mt-1" @click="removeImage(img)">
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </v-col>
          </v-row>

          <v-btn small color="blue darken-1" dark class="mt-2" @click="addImage">
            <v-icon left>mdi-plus</v-icon>
            Add Image
          </v-btn>
          <!-- Prepared By -->
          <v-divider class="my-4"></v-divider>
          <h4 class="text-subtitle-1 font-weight-medium mb-2">
            Prepared By
          </h4>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-combobox
                outlined
                dense
                hide-details="auto"
                label="Prepared By"
                v-model="preparedByObject"
                :items="preparedByDropdown"
                :error-messages="errors.prepared_by"
                :search-input.sync="preparedBySearch"
                no-filter
                return-object
                item-value="prepared_by"
              >
                <template #selection="{ item }">{{ item.prepared_by }}</template>
                <template #item="{ item }">
                  {{ item.prepared_by }} - <small>{{ item.prepared_by_position || 'N/A' }}</small>
                </template>
              </v-combobox>
            </v-col>

            <v-col cols="12" md="6">
              <v-combobox
                outlined
                dense
                hide-details="auto"
                label="Position"
                :items="preparedByPositions"
                v-model="inputs.prepared_by_position"
                :error-messages="errors.prepared_by_position"
              />
            </v-col>
          </v-row>

          <!-- Buttons -->
          <v-row dense class="mt-3">
            <v-col cols="12" sm="4">
              <v-btn block :disabled="!inputs.purchase_needed" @click="components.open = true">
                <v-icon left>mdi-cog</v-icon> Components
              </v-btn>
            </v-col>

            <v-col cols="12" sm="4">
              <v-btn block color="primary" dark @click="update">
                <v-icon left>mdi-content-save</v-icon> Update
              </v-btn>
            </v-col>
            <v-col cols="12" sm="4">
              <v-btn block color="blue darken-2" dark :to="'/downtime-reports'">
                <v-icon left>mdi-file-chart</v-icon> Downtime Reports
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Components Dialog -->
    <v-dialog v-model="components.open" max-width="600" persistent>
      <v-card>
        <v-card-title>Components</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" v-for="(component, i) in components.data" :key="i">
              <v-autocomplete
                outlined
                hide-details="auto"
                dense
                :label="`Component ${i + 1}`"
                v-model="components.data[i]"
                item-value="id"
                :item-text="item => `(${item.typeName} - ${item.categoryName}) ${item.name}`"
                :items="filteredMachineComponents"
                :error-messages="errors[`machine_components.${i}.machine_component_id`]"
                append-icon="mdi-delete"
                @click:append="removeComponent(i)"
              />
            </v-col>
          </v-row>
          <div class="d-flex justify-space-between mt-2">
            <v-btn small color="green" @click="components.data.push(null)">
              <v-icon left small>mdi-plus</v-icon>Add
            </v-btn>
            <v-btn small color="red" @click="components.open = false">
              <v-icon left small>mdi-close</v-icon>Close
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, SuccessPopup, ErrorPopup } from '@/libs/utils'
import { isAxiosError } from 'axios'
import _ from 'lodash'

export default {
  name: 'EditDowntimeReport',
  data() {
    return {
      spanOptions: [
        { label: '1/3', value: '1/3' },
        { label: '1/2', value: '1/2' },
        { label: '1', value: '1' },
      ],

      images: [],

      statusOptions: ['pending', 'ongoing', 'cancelled', 'completed'],

      inputs: {
        customer_id: null,
        subject: '',
        description: '',
        date_start: '',
        date_end: '',
        unit_id: '',
        machine_type_id: null,
        list_status_before: null,
        list_status_after: null,
        reported_issue: '',
        is_service_report: null,
        error: '',
        purchase_needed: false,
        reported_at: '',
        status: null,
        prepared_by: '',
        prepared_by_position: '',
      },

      preparedByObject: {},
      preparedByDropdown: [],
      preparedByPositions: [],
      preparedBySearch: '',

      inspectionFindings: [],
      recommendations: [],
      errors: {},
      customers: [],
      machineTypes: [],
      machineComponents: [],
      machineComponentCategories: [],
      actions: [],
      components: { open: false, data: [] },
    }
  },
  computed: {
    downtimeReportId() {
      return this.$route.params.downtimeReportId
    },
    mapMachineCategories() {
      return this.machineComponentCategories.reduce((map, c) => {
        map[c.id] = c.name
        return map
      }, {})
    },
    mapMachineTypes() {
      return this.machineTypes.reduce((map, t) => {
        map[t.id] = t.name
        return map
      }, {})
    },
    filteredMachineComponents() {
      return this.machineComponents.reduce((arr, comp) => {
        const categoryName = this.mapMachineCategories[comp.machine_component_category_id] || ''
        const typeName = this.mapMachineTypes[comp.machine_type_id] || ''
        if (!this.inputs.machine_type_id || comp.machine_type_id === this.inputs.machine_type_id) {
          arr.push({ ...comp, categoryName, typeName })
        }
        return arr
      }, [])
    },
    imageRows() {
      const rows = []
      let currentRow = []
      let currentSpan = 0

      this.images.forEach(img => {
        const span = this.spanToNumber(img.span)

        if (currentSpan + span > 1) {
          rows.push(currentRow)
          currentRow = []
          currentSpan = 0
        }

        currentRow.push(img)
        currentSpan += span
      })

      if (currentRow.length) {
        rows.push(currentRow)
      }

      return rows
    },
  },
  created() {
    this.fetchEditData()
    this.fetchCustomers()
    this.fetchOptions()
    this.fetchPositions()
    this.debouncedFetchOptions = _.debounce(this.fetchPreparedByDropdown, 300)
  },
  watch: {
    preparedBySearch() {
      this.debouncedFetchOptions()
    },
    preparedByObject(newVal) {
      if (newVal) {
        this.inputs.prepared_by = newVal.prepared_by
        this.inputs.prepared_by_position = newVal.prepared_by_position
        return
      }
      this.inputs.prepared_by = ''
      this.inputs.prepared_by_position = ''
    },
  },
  methods: {
    spanToNumber(span) {
      if (span === '1') return 1
      if (span === '1/2') return 0.5
      if (span === '1/3') return 1 / 3

      return 0.5
    },

    addImage() {
      this.images.push({
        id: null,
        name: '',
        span: '1/2',
        file: null,
        preview: null,
        new: true,
      })
    },

    removeImage(img) {
      const index = this.images.indexOf(img)

      if (index > -1) {
        this.images.splice(index, 1)
      }
    },

    onFileChange(file, img) {
      img.file = file
      img.preview = file ? URL.createObjectURL(file) : img.preview
    },

    getImageIndex(img) {
      return this.images.indexOf(img)
    },
    statusColor(status) {
      switch (status) {
        case 'pending':
          return 'orange lighten-2'
        case 'ongoing':
          return 'blue lighten-2'
        case 'completed':
          return 'green lighten-2'
        case 'cancelled':
          return 'red lighten-2'
        default:
          return 'grey lighten-2'
      }
    },
    /**
     * Description
     * @param {number} downtimeReportId
     */
    async printReport(downtimeReportId) {
      try {
        LoadingPopup.fire('Loading', 'Preparing Report...')
        const axios = appAxios()
        const { data } = await axios.get(`/v3/downtime-reports/${downtimeReportId}/pdf-v2`, {
          responseType: 'blob',
        })

        // Create a blob URL
        const fileURL = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        window.open(fileURL, '_blank') // open in new tab/window
        SuccessPopup.fire({
          title: 'Success',
          text: 'successfully generated the pdf',
          toast: true,
          showCloseButton: false,
          timer: 3000,
        })
      } catch (e) {
        ErrorPopup.fire('Error', e.message)
      }
    },
    async fetchEditData() {
      try {
        const axios = appAxios()

        const { data } = await axios.get(`/v3/downtime-reports/${this.downtimeReportId}/edit`)

        const report = data.data

        Object.keys(this.inputs).forEach(key => {
          this.inputs[key] = report[key]
        })

        this.inputs.purchase_needed = !!report.purchase_needed

        this.inspectionFindings = report.inspection_findings
          ? report.inspection_findings.split(/\r\n|\n|\r/)
          : []

        this.recommendations = report.recommendations
          ? report.recommendations.split(/\r\n|\n|\r/)
          : []

        this.actions = report.actions?.map(a => a.action) || []

        this.components.data = report.machine_components?.map(c => c.id) || []

        this.images = (report.images || []).map(img => ({
          id: img.id,
          name: img.name,
          span: img.span || '1/2',
          file: null,
          preview: img.preview_image,
          new: false,
        }))

        if (this.inputs.prepared_by) {
          this.preparedByObject = {
            prepared_by: this.inputs.prepared_by,
            prepared_by_position: this.inputs.prepared_by_position,
          }
        }
      } catch (e) {
        console.error(e)
        ErrorPopup.fire('Failed to load downtime report.')
      }
    },
    async fetchOptions() {
      const axios = appAxios()
      const { data } = await axios.get('/v3/machine-components/options')
      this.machineComponents = data?.components || []
      this.machineTypes = data?.types || []
      this.machineComponentCategories = data?.categories || []
    },
    async fetchCustomers() {
      const axios = appAxios()
      const { data } = await axios.get('/v3/customers/dropdown-options')
      this.customers = data.data || []
    },
    removeAction(i) {
      this.actions.splice(i, 1)
    },
    removeComponent(i) {
      this.components.data.splice(i, 1)
    },
    async update() {
      try {
        LoadingPopup.fire('Loading', 'Updating downtime report...')

        this.errors = {}

        const formData = new FormData()

        formData.append('_method', 'PUT')

        Object.entries(this.inputs).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value)
          }
        })

        formData.set('inspection_findings', this.inspectionFindings.filter(item => item).join('\n'))

        formData.set('recommendations', this.recommendations.filter(item => item).join('\n'))

        this.actions.forEach((action, i) => {
          formData.append(`actions[${i}][action]`, action)
        })

        this.components.data.forEach((id, i) => {
          formData.append(`machine_components[${i}][machine_component_id]`, id)
        })

        this.images.forEach((img, i) => {
          if (img.id) {
            formData.append(`images[${i}][id]`, img.id)
          }

          if (img.new) {
            formData.append(`images[${i}][new]`, '1')
          }

          formData.append(`images[${i}][name]`, img.name || '')

          formData.append(`images[${i}][span]`, img.span || '1/2')

          if (img.file) {
            formData.append(`images[${i}][file]`, img.file)
          }
        })

        const axios = appAxios()

        const { data } = await axios.post(
          `/v3/downtime-reports/${this.downtimeReportId}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        LoadingPopup.close()

        SuccessPopup.fire({
          title: 'Success',
          text: 'Downtime report updated successfully!',
          toast: true,
          timer: 3000,
          position: 'top-end',
        })

        if (data.report_status === 'completed') {
          this.$router.push(`/downtime-reports/${this.downtimeReportId}/show`)

          return
        }

        this.fetchEditData()
      } catch (error) {
        LoadingPopup.close()

        if (isAxiosError(error) && error.response?.status === 422) {
          this.errors = error.response.data.errors || {}

          return
        }

        ErrorPopup.fire('Failed to update downtime report.', error.message)
      }
    },
    async fetchPositions() {
      try {
        const axios = appAxios()
        const { data } = await axios.get('/v3/downtime-reports/prepared-by/positions')
        this.preparedByPositions = data.data || []
      } catch (e) {
        console.error('Error fetching prepared by positions:', e)
      }
    },

    async fetchPreparedByDropdown() {
      try {
        const axios = appAxios()
        const { data } = await axios.get('/v3/downtime-reports/prepared-by/dropdown', {
          params: { search: this.preparedBySearch },
        })
        this.preparedByDropdown = data.data || []
      } catch (e) {
        console.error('Error fetching prepared by dropdown:', e)
      }
    },
  },
}
</script>

<style scoped>
@media (max-width: 600px) {
  h1 {
    font-size: 1.25rem;
  }

  .v-card {
    padding: 12px !important;
  }

  .v-btn {
    min-width: 100%;
  }
}
</style>
